import { Icon, cx } from "../components/ui";
import { NAMA_PERAN, cabangUntukPeran } from "../utils/peranCabang";

// Layar pemilihan cabang setelah login (untuk user yang tergabung di >1 cabang).
// User 1 cabang otomatis melewati layar ini (lihat App.js).
//
// `branches` = cabang yang cocok dengan PERAN AKTIF (peran per cabang): masuk sebagai Team
// Leader hanya menawarkan cabang tempat ia team leader. Kosong padahal akunnya punya cabang →
// peran ini belum dipegang di cabang mana pun; tawarkan peran lain yang punya cabang.
export default function BranchPicker({
  user,
  branches: daftar,
  role = null,
  onSwitchRole,
  onSelectBranch,
  onLogout,
  theme,
  toggleTheme
}) {
  const branches = daftar || user?.branches || [];
  const peranAkun = Array.isArray(user?.roles) ? user.roles : [];
  const peranLain = peranAkun.filter((r) => r !== role && cabangUntukPeran(user, r).length > 0);
  const namaPeran = NAMA_PERAN[role] || role;
  const punyaCabang = (user?.branches || []).length > 0;

  return (
    <section className="relative min-h-screen overflow-hidden px-4 py-8">
      <div className="pointer-events-none absolute -left-20 -top-20 h-96 w-96 rounded-full bg-primary/20 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-20 -right-20 h-96 w-96 rounded-full bg-primary/20 blur-[120px]" />

      <button
        type="button"
        onClick={toggleTheme}
        title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        className="absolute right-4 top-4 z-20 rounded-xl border border-slate-200 bg-white/80 p-2.5 text-slate-500 shadow-lg backdrop-blur hover:text-primary dark:border-slate-700 dark:bg-slate-900/70"
      >
        <Icon name={theme === "dark" ? "light_mode" : "dark_mode"} />
      </button>

      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-4rem)] max-w-4xl flex-col items-center justify-center">
        <div className="mb-2 flex items-center gap-3 text-center">
          <Icon name="store" className="text-4xl text-primary neon-text-glow" />
          <h1 className="text-2xl font-black tracking-tight text-slate-900 neon-text-glow dark:text-white">
            Pilih Cabang
          </h1>
        </div>
        <p className="mb-8 text-sm text-slate-500">
          Halo {user?.nickname || user?.name} — pilih cabang yang ingin kamu buka
          {peranAkun.length > 1 && role ? ` sebagai ${namaPeran}` : ""}.
        </p>

        {branches.length === 0 ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10">
            <Icon name="info" className="mb-2 text-3xl" />
            <p className="text-sm font-bold">
              {punyaCabang && role
                ? `Akun ini belum menjadi ${namaPeran} di cabang mana pun.`
                : "Akun ini belum ditugaskan ke cabang mana pun."}
            </p>
            <p className="mt-1 text-xs">
              Hubungi admin untuk penugasan cabang.
            </p>
            {punyaCabang && peranLain.length > 0 && onSwitchRole && (
              <div className="mt-4 flex flex-wrap justify-center gap-2" role="group" aria-label="Masuk sebagai peran lain">
                {peranLain.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => onSwitchRole(r)}
                    className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-bold text-amber-800 hover:border-primary hover:text-primary dark:border-amber-500/30 dark:bg-slate-900 dark:text-amber-200"
                  >
                    Masuk sebagai {NAMA_PERAN[r] || r}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="grid w-full gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {branches.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => onSelectBranch(b)}
                className={cx(
                  "group flex flex-col items-start rounded-2xl border border-slate-200 bg-white/95 p-5 text-left shadow-lg backdrop-blur transition",
                  "hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-xl",
                  "dark:border-primary/10 dark:bg-slate-900/60"
                )}
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
                  <Icon name="store" />
                </div>

                <div className="flex w-full items-center justify-between gap-2">
                  <span className="text-xs font-black uppercase tracking-wider text-primary">
                    {b.code}
                  </span>
                  {b.coverage ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
                      Coverage
                    </span>
                  ) : b.role ? (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500 dark:bg-slate-800">
                      {b.role}
                    </span>
                  ) : null}
                </div>

                <h3 className="mt-1 text-lg font-black">{b.name || b.code}</h3>

                <span className="mt-4 flex items-center gap-1 text-sm font-bold text-primary opacity-0 transition group-hover:opacity-100">
                  Buka
                  <Icon name="arrow_forward" className="text-base" />
                </span>
              </button>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={onLogout}
          className="mt-10 flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-red-500"
        >
          <Icon name="logout" className="text-base" />
          Keluar
        </button>
      </div>
    </section>
  );
}
